import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { UserInfo } from '@/api/client/types.gen'

export interface AuthState {
  isValid: boolean
  userInfo: UserInfo | null
  message: string | null
  isLoading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const isValid = ref<boolean>(false)
  const userInfo = ref<UserInfo | null>(null)
  const message = ref<string | null>(null)
  const isLoading = ref<boolean>(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => isValid.value && !!userInfo.value)
  const accessToken = computed(() => userInfo.value?.accessToken || null)
  const culture = computed(() => userInfo.value?.culture || null)
  const canCompose = computed(() => userInfo.value?.canCompose || false)
  const canApprove = computed(() => userInfo.value?.canApprove || false)
  const userName = computed(() => userInfo.value?.name || null)
  const userEmail = computed(() => userInfo.value?.email || null)
  const sessionId = computed(() => userInfo.value?.sessionId || null)
  const sessionUrl = computed(() => userInfo.value?.sessionUrl || null)

  // Actions
  function setAuthData(authData: {
    isValid: boolean
    userInfo?: UserInfo
    message?: string | null
  }) {
    isValid.value = authData.isValid
    userInfo.value = authData.userInfo || null
    message.value = authData.message || null
    error.value = null
    isLoading.value = false
  }

  function setLoading(loading: boolean) {
    isLoading.value = loading
  }

  function setError(errorMessage: string) {
    error.value = errorMessage
    isLoading.value = false
  }

  function clearAuth() {
    isValid.value = false
    userInfo.value = null
    message.value = null
    error.value = null
    isLoading.value = false
  }

  function hasPermission(permission: 'compose' | 'approve'): boolean {
    if (!isAuthenticated.value) return false
    
    switch (permission) {
      case 'compose':
        return canCompose.value
      case 'approve':
        return canApprove.value
      default:
        return false
    }
  }

  return {
    // State
    isValid: computed(() => isValid.value),
    userInfo: computed(() => userInfo.value),
    message: computed(() => message.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // Computed getters
    isAuthenticated,
    accessToken,
    culture,
    canCompose,
    canApprove,
    userName,
    userEmail,
    sessionId,
    sessionUrl,

    // Actions
    setAuthData,
    setLoading,
    setError,
    clearAuth,
    hasPermission,
  }
})